/**
 * S3UploadManagerWrapper.tsx
 * 
 * Wrapper component that creates and provides the global UploadJobManager instance
 * Must be nested within S3UploadProvider and have access to S3Service
 */

import React, { useState, useEffect, useRef, type ReactNode } from 'react';
import { S3UploadManagerProvider } from '../../contexts/S3UploadManagerContext';
import { useS3Upload as useS3UploadContext } from '../../contexts/S3UploadContext';
import { useS3Service } from '../../hooks/useS3Service';
import { createUploadJobManager, type UploadJobManager } from '../../services/uploadJobManager';

// ============================================================================
// Props Types
// ============================================================================

export interface S3UploadManagerWrapperProps {
  children: ReactNode;
  /** Maximum concurrent uploads (default: 5) */
  maxConcurrent?: number;
  /** Timeout per file in milliseconds (default: 5 minutes) */
  fileTimeout?: number;
  /** Maximum retries per file (default: 3) */
  maxRetries?: number;
}

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * S3UploadManagerWrapper
 * 
 * Creates and provides the global UploadJobManager instance.
 * This must be nested within S3UploadProvider.
 */
export const S3UploadManagerWrapper: React.FC<S3UploadManagerWrapperProps> = ({
  children,
  maxConcurrent = 5,
  fileTimeout = 300000,
  maxRetries = 3,
}) => {
  const { s3Service } = useS3Service();
  const uploadContext = useS3UploadContext();
  const [manager, setManager] = useState<UploadJobManager | null>(null);
  
  // Use a ref to always point to the latest uploadContext
  const uploadContextRef = useRef(uploadContext);
  uploadContextRef.current = uploadContext;

  useEffect(() => {
    if (s3Service) {
      console.log('[S3UploadManager] Initializing upload manager', {
        hasS3Service: !!s3Service,
        s3ServiceType: typeof s3Service,
        s3ServiceConstructor: s3Service.constructor.name,
        hasUploadFile: typeof s3Service.uploadFile,
        s3ServiceKeys: Object.keys(s3Service),
        s3ServiceProto: Object.getOwnPropertyNames(Object.getPrototypeOf(s3Service)),
      });
      
      // WORKAROUND: Fix missing upload methods due to webpack/browser caching issue
      if (typeof s3Service.uploadFile !== 'function') {
        console.warn('[S3UploadManager] uploadFile missing! Attempting to bind from prototype...');
        const proto = Object.getPrototypeOf(s3Service);
        
        if (typeof proto.uploadFile === 'function') {
          console.log('[S3UploadManager] Found uploadFile on prototype, binding to instance');
          (s3Service as any).uploadFile = proto.uploadFile.bind(s3Service);
          (s3Service as any).uploadFileMultipart = proto.uploadFileMultipart?.bind(s3Service);
          (s3Service as any).uploadFiles = proto.uploadFiles?.bind(s3Service);
          (s3Service as any).abortUpload = proto.abortUpload?.bind(s3Service);
          console.log('[S3UploadManager] Upload methods bound successfully', {
            hasUploadFile: typeof s3Service.uploadFile,
          });
        } else {
          console.error('[S3UploadManager] CRITICAL: uploadFile NOT on prototype either!', {
            protoMethods: Object.getOwnPropertyNames(proto),
          });
        }
      } else {
        console.log('[S3UploadManager] uploadFile already exists on instance');
      }
      
      // Create a proxy that always uses the latest context
      const contextProxy = new Proxy({} as any, {
        get: (target, prop) => {
          return uploadContextRef.current[prop as keyof typeof uploadContext];
        }
      });
      
      console.log('[S3UploadManager] Creating upload job manager with s3Service:', s3Service);
      
      const newManager = createUploadJobManager(s3Service, contextProxy, {
        maxConcurrent,
        fileTimeout,
        maxRetries,
      });
      setManager(newManager);
      console.log('[S3UploadManager] Upload manager initialized successfully');

      return () => {
        // Cleanup on unmount
        console.log('[S3UploadManager] Shutting down upload manager');
        newManager.shutdown().catch((err: unknown) => {
          console.error('Error shutting down upload manager:', err);
        });
      };
    } else {
      console.warn('[S3UploadManager] Waiting for s3Service', { 
        hasS3Service: !!s3Service
      });
    }
    // Only recreate when s3Service or config changes
  }, [s3Service, maxConcurrent, fileTimeout, maxRetries]);

  return (
    <S3UploadManagerProvider manager={manager}>
      {children}
    </S3UploadManagerProvider>
  );
};

export default S3UploadManagerWrapper;
